// src/components/ml/FlowSenseAIGuide.js
// --- UPDATE (round 3, new file) ------------------------------------------------------------------
// FlowSense AI Guide — a route-aware guide to the page the user is on and the AI that runs on it.
//
//   <FlowSenseAIGuide />                          "AI Guide" button → drawer (uses the current route)
//   <FlowSenseAIGuide tab="predictive-health" />  Analytics Hub: also the open tab
//   <FlowSenseAIGuideLauncher />                  floating button for the admin layout (every page)
//
// The drawer has four parts, all from GET /ml-ops/guide (so it describes what is really deployed):
//   Tour       – step-by-step; "Show me" scrolls to and highlights the element (data-guide="…")
//   AI here    – each model / engine on the page with its live status (office roles): production
//                version and metric, training now, challenger waiting, engine online / offline
//   Ask        – quick questions and the Ask FlowSense AI chat, focused on this module
//   Terms      – the glossary for the page
// It refetches when the route or tab changes and when `liveVersion` changes (live AI events).
// --- END UPDATE ------------------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
  DrawerOverlay, Flex, HStack, Icon, IconButton, Progress, Skeleton, Stack, Tab, TabList, TabPanel, TabPanels,
  Tabs, Text, Tooltip, VStack, Wrap, WrapItem, useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import {
  MdArrowBack, MdArrowForward, MdAutoAwesome, MdCheckCircle, MdExplore, MdMyLocation, MdPsychology,
  MdRefresh, MdSchool, MdWarningAmber,
} from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import mlOpsService from '../../services/mlOpsService';
import FlowSenseAIExplainer, { ExplainButton } from './FlowSenseAIExplainer';
import { ENGINE_COLOR, fmtMetric, timeAgo } from './mlFormat';

const SEEN_KEY = 'flowsense.aiGuide.seen';
const readSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') || {}; } catch (_) { return {}; } };
const markSeen = (key) => { try { const s = readSeen(); s[key] = Date.now(); localStorage.setItem(SEEN_KEY, JSON.stringify(s)); } catch (_) { /* storage blocked */ } };

const LIVE_STATE = {
  production: { label: 'In production', color: 'green' },
  training: { label: 'Training now', color: 'blue' },
  no_production: { label: 'No production version', color: 'orange' },
};

/** Loads the guide for `path` / `tab`; refetches when either or `liveVersion` changes. */
export function useAIGuide({ path, tab = null, liveVersion = 0, enabled = true }) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [nonce, setNonce] = useState(0);
  const lastLive = useRef(liveVersion);

  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    setState(s => ({ ...s, loading: true, error: null }));
    mlOpsService.getGuide({ path, tab, signal: ctrl.signal })
      .then((data) => { if (alive) setState({ data, loading: false, error: null }); })
      .catch((e) => { if (alive && e.name !== 'AbortError' && e.name !== 'CanceledError') setState(s => ({ ...s, loading: false, error: e.message || 'Could not load the guide.' })); });
    return () => { alive = false; ctrl.abort(); };
  }, [path, tab, nonce, enabled]);

  // live AI events: refresh at most every 20 s
  useEffect(() => {
    if (!enabled || liveVersion === lastLive.current) return undefined;
    lastLive.current = liveVersion;
    const t = setTimeout(() => setNonce(n => n + 1), 20000);
    return () => clearTimeout(t);
  }, [liveVersion, enabled]);

  const refresh = useCallback(() => setNonce(n => n + 1), []);
  return { ...state, refresh };
}

/** Scrolls to [data-guide="target"] and flashes an outline around it. Returns false when absent. */
export function highlightGuideTarget(target) {
  if (!target || typeof document === 'undefined') return false;
  const el = document.querySelector(`[data-guide="${target}"]`);
  if (!el) return false;
  try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { /* old browsers */ }
  const prev = { outline: el.style.outline, outlineOffset: el.style.outlineOffset, transition: el.style.transition };
  el.style.transition = 'outline-color 0.3s';
  el.style.outline = '3px solid #805AD5';
  el.style.outlineOffset = '4px';
  setTimeout(() => { el.style.outline = prev.outline; el.style.outlineOffset = prev.outlineOffset; el.style.transition = prev.transition; }, 2600);
  return true;
}

function AiCard({ item, onAsk }) {
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const live = item.live;
  let badge = null;
  if (live) {
    if (item.kind === 'trainable') {
      const meta = LIVE_STATE[live.state] || { label: live.state, color: 'gray' };
      badge = <Badge colorScheme={meta.color}>{meta.label}</Badge>;
    } else {
      badge = <Badge colorScheme={ENGINE_COLOR[live.state] || 'gray'}>{String(live.state || 'unknown').replace('_', ' ')}</Badge>;
    }
  }
  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="lg" p={3} data-testid={`guide-ai-${item.key}`}>
      <Flex justify="space-between" align="flex-start" gap={2}>
        <Box minW={0}>
          <HStack spacing={2} mb={1} flexWrap="wrap">
            <Text fontWeight="600" fontSize="sm">{item.name}</Text>
            {badge}
          </HStack>
          <Text fontSize="xs" color={muted}>{item.short}</Text>
        </Box>
        <ExplainButton topic={item.key} label="" aria-label={`Explain ${item.name}`} />
      </Flex>
      {live && item.kind === 'trainable' && (
        <Wrap mt={2} spacing={3} fontSize="xs" color={muted}>
          {live.version && <WrapItem>Version <Text as="span" fontWeight="600" ml={1}>{live.version}</Text></WrapItem>}
          {live.metric !== null && live.metric !== undefined && (
            <WrapItem>{live.metricLabel || 'Metric'}: <Text as="span" fontWeight="600" ml={1}>{fmtMetric(live.metric, { format: live.metricFormat })}</Text></WrapItem>
          )}
          {live.trainedAt && <WrapItem>Trained {timeAgo(live.trainedAt)}</WrapItem>}
          {live.newRecords !== null && live.newRecords !== undefined && <WrapItem>{live.newRecords} new records since</WrapItem>}
          {live.challengerWaiting && <WrapItem><Badge colorScheme="purple">v{live.challengerWaiting} waiting for approval</Badge></WrapItem>}
          {live.lastRun?.status === 'failed' && <WrapItem><Badge colorScheme="red">Last training failed</Badge></WrapItem>}
        </Wrap>
      )}
      {live && item.kind !== 'trainable' && live.detail && <Text mt={2} fontSize="xs" color={muted}>{live.detail}</Text>}
      {item.feedbackLoop && <Text mt={2} fontSize="xs" color={muted}><Icon as={MdSchool} mr={1} />{item.feedbackLoop}</Text>}
      <Button mt={2} size="xs" variant="link" colorScheme="purple" onClick={() => onAsk(`How does ${item.name} work on this page, and how reliable is it right now?`)}>Ask about it</Button>
    </Box>
  );
}

/** The guide body (also usable inline, e.g. in a page's help tab). */
export function FlowSenseAIGuidePanel({ path, tab = null, liveVersion = 0, onAsk: onAskProp = null, initialTab = 0, onNavigateModule = null }) {
  const { data, loading, error, refresh } = useAIGuide({ path, tab, liveVersion });
  const [step, setStep] = useState(0);
  const [view, setView] = useState(initialTab);
  const [askState, setAskState] = useState({ key: 0, question: null });
  const [missing, setMissing] = useState({});
  const muted = useColorModeValue('gray.600', 'gray.400');
  const stepBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const border = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => { setStep(0); setMissing({}); }, [path, tab]);

  const steps = data?.steps || [];
  const current = steps[Math.min(step, Math.max(steps.length - 1, 0))];

  const ask = useCallback((question) => {
    if (onAskProp) { onAskProp(question); return; }
    setAskState(s => ({ key: s.key + 1, question }));
    setView(2);
  }, [onAskProp]);

  const showMe = useCallback((target) => {
    const ok = highlightGuideTarget(target);
    if (!ok) setMissing(m => ({ ...m, [target]: true }));
  }, []);

  if (loading && !data) {
    return <VStack align="stretch" spacing={3}><Skeleton h="20px" /><Skeleton h="60px" /><Skeleton h="60px" /></VStack>;
  }
  if (error && !data) {
    return (
      <Alert status="error" borderRadius="md" fontSize="sm">
        <AlertIcon />
        <Box flex="1">{error}</Box>
        <Button size="xs" leftIcon={<MdRefresh />} onClick={refresh}>Retry</Button>
      </Alert>
    );
  }
  if (!data) return null;

  return (
    <VStack align="stretch" spacing={3}>
      <Box>
        <HStack justify="space-between" align="flex-start">
          <Box>
            <Text fontWeight="700">{data.module.name}{data.section ? ` · ${data.section.name}` : ''}</Text>
            <Text fontSize="xs" color={muted}>{data.module.short}</Text>
          </Box>
          <Tooltip label="Refresh live status" hasArrow>
            <IconButton size="xs" variant="ghost" icon={<MdRefresh />} aria-label="Refresh guide" onClick={refresh} isLoading={loading} />
          </Tooltip>
        </HStack>
        {data.live && (
          <HStack mt={2} spacing={2} flexWrap="wrap">
            <Badge colorScheme={data.engineOnline ? 'green' : 'red'}>ML engine {data.engineOnline ? 'online' : 'offline'}</Badge>
            <Badge colorScheme={data.assistantOnline ? 'green' : 'gray'}>LLM {data.assistantOnline ? 'online' : 'offline — built-in answers'}</Badge>
          </HStack>
        )}
      </Box>

      {data.attention?.length > 0 && (
        <Alert status="warning" borderRadius="md" fontSize="xs" alignItems="flex-start" data-testid="guide-attention">
          <AlertIcon as={MdWarningAmber} />
          <VStack align="stretch" spacing={1}>
            {data.attention.map((a, i) => <Text key={i}>{a.text}</Text>)}
          </VStack>
        </Alert>
      )}

      <Tabs index={view} onChange={setView} size="sm" variant="enclosed" isLazy>
        <TabList>
          <Tab>Tour</Tab>
          <Tab>AI here ({data.ai.length})</Tab>
          <Tab>Ask</Tab>
          <Tab>Terms</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            {steps.length === 0 ? <Text fontSize="sm" color={muted}>No tour for this page yet — use Ask.</Text> : (
              <VStack align="stretch" spacing={3}>
                <Progress value={((step + 1) / steps.length) * 100} size="xs" colorScheme="purple" borderRadius="full" />
                <Box bg={stepBg} borderRadius="lg" p={4} data-testid="guide-step">
                  <Text fontSize="xs" color={muted} mb={1}>Step {step + 1} of {steps.length}</Text>
                  <Text fontWeight="700" mb={1}>{current.title}</Text>
                  <Text fontSize="sm">{current.body}</Text>
                  <HStack mt={3} spacing={2} flexWrap="wrap">
                    {current.target && (
                      <Button size="xs" leftIcon={<MdMyLocation />} colorScheme="purple" variant="outline" onClick={() => showMe(current.target)} isDisabled={missing[current.target]}>
                        {missing[current.target] ? 'Not on screen' : 'Show me'}
                      </Button>
                    )}
                    {current.ai && <ExplainButton topic={current.ai} label="Explain the AI" />}
                  </HStack>
                </Box>
                <HStack justify="space-between">
                  <Button size="sm" leftIcon={<MdArrowBack />} variant="ghost" onClick={() => setStep(s => Math.max(s - 1, 0))} isDisabled={step === 0}>Back</Button>
                  {step < steps.length - 1
                    ? <Button size="sm" rightIcon={<MdArrowForward />} colorScheme="purple" onClick={() => setStep(s => s + 1)}>Next</Button>
                    : <Button size="sm" leftIcon={<MdCheckCircle />} colorScheme="green" onClick={() => setView(1)}>See the AI here</Button>}
                </HStack>
                <Stack spacing={1}>
                  {steps.map((s, i) => (
                    <Button key={s.id} size="xs" variant={i === step ? 'solid' : 'ghost'} justifyContent="flex-start" onClick={() => setStep(i)} fontWeight={i === step ? '700' : '400'}>
                      {i + 1}. {s.title}
                    </Button>
                  ))}
                </Stack>
              </VStack>
            )}
          </TabPanel>
          <TabPanel px={0}>
            <VStack align="stretch" spacing={2}>
              {!data.live && <Text fontSize="xs" color={muted}>Live model status is shown to office roles.</Text>}
              {data.ai.length === 0 && <Text fontSize="sm" color={muted}>No AI models run on this page.</Text>}
              {data.ai.map(item => <AiCard key={item.key} item={item} onAsk={ask} />)}
            </VStack>
          </TabPanel>
          <TabPanel px={0}>
            <VStack align="stretch" spacing={3}>
              <Wrap spacing={2}>
                {(data.questions || []).map(q => (
                  <WrapItem key={q}><Button size="xs" variant="outline" colorScheme="purple" borderRadius="full" onClick={() => ask(q)}>{q}</Button></WrapItem>
                ))}
              </Wrap>
              {!onAskProp && (
                <FlowSenseAIExplainer
                  key={askState.key}
                  focus={data.module.key}
                  initialQuestion={askState.question}
                  height="420px"
                  showFocusPicker={false}
                  intro={`You are on ${data.module.name}${data.section ? ` (${data.section.name})` : ''}.`}
                />
              )}
            </VStack>
          </TabPanel>
          <TabPanel px={0}>
            <VStack align="stretch" spacing={2}>
              {(data.glossary || []).length === 0 && <Text fontSize="sm" color={muted}>No special terms on this page.</Text>}
              {(data.glossary || []).map(g => (
                <Box key={g.term} borderBottomWidth="1px" borderColor={border} pb={2}>
                  <Text fontWeight="600" fontSize="sm" textTransform="capitalize">{g.term.replace(/_/g, ' ')}</Text>
                  <Text fontSize="xs" color={muted}>{g.text}</Text>
                </Box>
              ))}
              {onNavigateModule && (
                <Box pt={2}>
                  <Text fontSize="xs" color={muted} mb={1}>Guides for other modules:</Text>
                  <Wrap spacing={1}>
                    {(data.modules || []).map(m => (
                      <WrapItem key={m.key}><Button size="xs" variant="ghost" onClick={() => onNavigateModule(m.path)}>{m.name}</Button></WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}

/**
 * "AI Guide" button + drawer for the current page.
 * Props: path (default: current route), tab, liveVersion, onAsk (send questions to the page's own
 * Ask drawer instead of the built-in chat), label, size, variant.
 */
export default function FlowSenseAIGuide({ path: pathProp = null, tab = null, liveVersion = 0, onAsk = null, label = 'AI Guide', size = 'sm', variant = 'outline', ...rest }) {
  const location = useLocation();
  const path = pathProp || location?.pathname || '/admin/dashboard';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const seenKey = `${path}${tab ? `?${tab}` : ''}`;
  const [isNew, setIsNew] = useState(() => !readSeen()[seenKey]);
  useEffect(() => { setIsNew(!readSeen()[seenKey]); }, [seenKey]);

  const open = useCallback(() => { onOpen(); markSeen(seenKey); setIsNew(false); }, [onOpen, seenKey]);
  const handleAsk = useMemo(() => (onAsk ? (q) => { onClose(); onAsk(q); } : null), [onAsk, onClose]);

  return (
    <>
      <Tooltip label="Step-by-step guide to this page and the AI on it" hasArrow>
        <Box position="relative" display="inline-block">
          <Button size={size} variant={variant} colorScheme="purple" leftIcon={<MdExplore />} onClick={open} data-testid="ai-guide-button" {...rest}>{label}</Button>
          {isNew && <Box position="absolute" top="-3px" right="-3px" w="9px" h="9px" borderRadius="full" bg="pink.400" data-testid="ai-guide-new" />}
        </Box>
      </Tooltip>
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack><Icon as={MdPsychology} color="purple.400" /><Text>FlowSense AI Guide</Text></HStack>
          </DrawerHeader>
          <DrawerBody py={4}>
            {isOpen && <FlowSenseAIGuidePanel path={path} tab={tab} liveVersion={liveVersion} onAsk={handleAsk} />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/** Floating launcher for the admin layout: one AI Guide on every page. */
export function FlowSenseAIGuideLauncher({ bottom = '24px', right = '24px', ...rest }) {
  const shadow = useColorModeValue('lg', 'dark-lg');
  return (
    <Box position="fixed" bottom={bottom} right={right} zIndex={1300} boxShadow={shadow} borderRadius="full" className="no-print">
      <FlowSenseAIGuide variant="solid" size="md" borderRadius="full" leftIcon={<MdAutoAwesome />} {...rest} />
    </Box>
  );
}
